import _ from 'lodash';
import { createSlice } from '@reduxjs/toolkit';

import { changeGlobalParameter } from 'shared/product_tabs/tab_project/actions/changeGlobalParameter';
import {
  isParameterNameQtyValid,
  isProductTypeParameterNameQtyValid,
} from 'shared/utils/validateQuantities';
import {
  START,
  QUOTING,
  QUOTED,
  PRICE_SELECTED,
  ERROR,
  CAN_NOT_QUOTE_AUTOMATICALLY,
} from 'shared/data/quoteSteps';

import initialState from 'us_full_feature_pcb/pcb_fabrication/data/initialState';
import defaultState from 'us_full_feature_pcb/pcb_fabrication/data/defaultState';
import defaultQuote from 'us_full_feature_pcb/pcb_fabrication/data/defaultQuote';
import { shouldForceRequote } from 'shared/utils/shouldForceRequote';
import { changeParameterByName } from 'us_full_feature_pcb/pcb_fabrication/slice/onChangeParameter';
import { validator } from 'us_full_feature_pcb/pcb_fabrication/slice/validators';
import { doEmailQuote, doQuote } from 'us_full_feature_pcb/pcb_fabrication/slice/thunks';
import { shouldUpdatePanelSize, updatePanelSize } from 'us_full_feature_pcb/pcb_fabrication/slice/utils';
import { finishPlatingOptions, materialTypeOptions } from 'shared/data/selectOptions';

const fabricationSlice = createSlice({
  name: 'fabricationSlice',
  initialState: {
    ...initialState,
    validatorFlag: false,
  },
  reducers: {
    updateValidatorFlag: (state) => {
      state.validatorFlag = !state.validatorFlag;
    },
    changeParameter: (state, action) => {
      const { name, value } = action.payload;
      changeParameterByName(state.parameters, name, value);

      state.parametersChanged = shouldForceRequote(name);

      if (shouldUpdatePanelSize(name)) {
        updatePanelSize(state);
      }

      if (isParameterNameQtyValid(name)) {
        if (!state.quantitiesChanged) state.quantitiesChanged = true;

        if (state.quote.line_items.length > 0 && state.quoteStep === START) {
          const qty_index = Number(name.split('qty')[1]) - 1;

          state.quote.line_items.forEach((li) => {
            li.quantities[qty_index] = value;
          });
        }
      }

      if (name === 'finish_plating') {
        if (value === finishPlatingOptions[0].value) {
          state.parameters.material_type = materialTypeOptions[1].value;
        } else {
          state.parameters.material_type = materialTypeOptions[0].value;
        }
      }
    },
    priceSelected: (state, action) => {
      const sp = action.payload;
      state.parameters.row_selected = sp.row_selected;
      state.parameters.column_selected = sp.column_selected;
      state.parameters.quote_config_key = sp.quote_config_key;
      state.parameters.supplier_selected = sp.supplier;
      state.parameters.vendor_selected = sp.supplier;
      state.quoteStep = PRICE_SELECTED;
    },
    deleteFabrication: (state) => {
      const { currentVariant, productToCompare } = state;
      const { part_number, revision, filename } = state.parameters;
      validator.hideMessages();

      const newState = _.cloneDeep({
        ...defaultState,
        validatorFlag: false,
        currentVariant,
        productToCompare,
      });

      newState.parameters.part_number = part_number;
      newState.parameters.revision = revision;
      newState.parameters.filename = filename;
      newState.lineItemId = state.lineItemId;
      newState.destroy = true;

      return newState;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(changeGlobalParameter, (state, action) => {
        const { name, value } = action.payload;
        if (isProductTypeParameterNameQtyValid(name, 'assembly') &&
          !state.quantitiesChanged && state.quoteStep === START) {
          const qty_index = Number(name.split('_qty')[1]) - 1;
          state.parameters.quantities[qty_index] = value;
        }
      })
      .addCase(doQuote.pending, (state) => {
        state.loading = true;
        state.quoteStep = QUOTING;
      })
      .addCase(doQuote.fulfilled, (state, action) => {
        const quote = action.payload;
        state.loading = false;
        state.parametersChanged = false;

        state.parameters.product_quote_number = quote.number;
        state.parameters.row_selected = '';
        state.parameters.column_selected = '';
        state.parameters.quote_config_key = '';
        state.parameters.supplier_selected = '';
        state.parameters.vendor_selected = '';

        if (!quote || !quote.line_items || quote.line_items.length <= 0) {
          state.quoteStep = ERROR;
        } else {
          state.quoteStep = QUOTED;
        }

        state.quote = quote;
      })
      .addCase(doQuote.rejected, (state, action) => {
        const { code } = action.payload;
        state.loading = false;
        state.quote = defaultQuote;

        if (code === 450) {
          state.quoteStep = CAN_NOT_QUOTE_AUTOMATICALLY;
        } else {
          state.quoteStep = ERROR;
        }
      })
      .addCase(doEmailQuote.pending, (state) => {
        state.loading = true;
      })
      .addCase(doEmailQuote.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(doEmailQuote.rejected, (state) => {
        state.loading = false;
        state.emailQuoteError = true;
      });
  },
});

export const {
  updateValidatorFlag,
  changeParameter,
  priceSelected,
  deleteFabrication,
} = fabricationSlice.actions;

export default fabricationSlice.reducer;
